import Papa from "papaparse";
import { debounce } from "./utils";

const SELECTORS = {
  CONTAINER: "[data-glossary]",
  HERO: ".glossary__hero", 
  CONTENT: ".glossary__content",
  STATUS: ".glossary__status",
  CONTROLS: ".glossary__controls",
  ICON_DATA: "[data-glossary-icons]" 
};

const DATA_ATTRS = {
  CSV_URL: "data-glossary-csv",
  ZONE_CSV_URL: "data-hardiness-csv"
};

// --- CLEANED SVG CONSTANTS ---
const LEAF_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 12" width="13" height="12" class="level-icon">
  <path fill="#058a57" d="M6.36,10.34v1.21c0,.25.2.45.45.45s.45-.2.45-.45v-1.78c.04-.78.03-3.21-1.44-4.9C3.98,2.74.72,2.12.58,2.09c-.12-.02-.25,0-.35.08-.1.07-.17.18-.19.3-.02.12-.44,2.98,1.41,5.17,1.49,1.76,3.97,2.49,4.89,2.71h0Z"/>
  <path fill="#058a57" d="M7.7,7.49c.06.31.08.58.09.82l2.09-4.41c.07-.15.25-.21.4-.14.15.07.22.25.15.4l-2.55,5.37v.62s.05,0,.08,0c.07,0,.15-.02.21-.05.51-.27,3.12-1.74,4.18-3.86,1.4-2.79.11-5.84.05-5.97-.1-.22-.35-.33-.58-.24-.15.06-3.6,1.4-4.99,4.16l-.13.26c-.06.13-.06.28,0,.41,0,.02.76,1.43,1,2.63h0Z"/>
</svg>
`;

const LETTERS = ["#"].concat("ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""));

export class WsGlossary {
  constructor(container) {
    this.container = container;
    this.csvUrl = container.getAttribute(DATA_ATTRS.CSV_URL) || "";
    this.elContent = container.querySelector(SELECTORS.CONTENT);
    this.elStatus = container.querySelector(SELECTORS.STATUS);
    
    // 1. LOAD ICONS FROM SHOPIFY CONFIG
    this.categoryImages = {};
    const iconDataScript = container.querySelector(SELECTORS.ICON_DATA);
    if (iconDataScript) {
      try {
        this.categoryImages = JSON.parse(iconDataScript.textContent);
      } catch (e) {
        console.error("Error parsing glossary icon JSON", e);
      }
    }

    this.defaultPlaceholder = "https://placehold.co/100x100/f5f5f5/999?text=Plant";

    // Create/Find Controls Container
    let controls = container.querySelector(SELECTORS.CONTROLS);
    if (!controls) {
      controls = document.createElement("div");
      controls.className = "glossary__controls";
      this.container.insertBefore(controls, this.elContent); 
    }
    this.elControls = controls;

    this.allRows = [];
    this.activeFilters = {};
    this.searchTerm = ""; // lowercased product-name query, only set once >= 3 chars

    // Ranges for slider default
    this.harvestMin = 0;
    this.harvestMax = 365;
  }

  init() {
    if (!this.csvUrl) {
      this.setStatus("No CSV URL set.");
      return;
    }
    if (!this.elContent || !this.elStatus) return;
    
    this.initScrollListener();
    this.loadCsv();
  }

  updateSliderFill(el) {
    if (!el) return;
    const val = (el.value - el.min) / (el.max - el.min) * 100;
    el.style.background = `linear-gradient(to right, #164734 ${val}%, rgba(22, 71, 52, 0.15) ${val}%)`;
  }

  initScrollListener() {
    let lastScrollTop = 0;
    window.addEventListener("scroll", () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        this.elControls.classList.add("is-scrolling-down");
      } else {
        this.elControls.classList.remove("is-scrolling-down");
      }
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }, { passive: true });
  }

  setStatus(msg) {
    this.elStatus.textContent = msg || "";
  }

  normalizeHeader(h) {
    const cleaned = String(h || "").replace(/\s+/g, " ").trim();
    if (cleaned === "Unnamed: 0") return "Product Name";
    if (cleaned.startsWith("Light")) return "Light"; 
    if (cleaned.startsWith("Planting Location")) return "Location";
    if (cleaned.startsWith("Drought")) return "Drought";
    if (cleaned.startsWith("Best Season")) return "Season";
    if (cleaned.startsWith("Days to Harvest")) return "Harvest";
    if (cleaned.startsWith("Growing Zone") || cleaned === "Zone" || cleaned === "Zones" || cleaned.startsWith("Hardiness Zone")) return "Growing Zone";
    if (cleaned.startsWith("Transplant Month")) return "Transplant Month";
    if (cleaned.startsWith("Organic")) return "Organic";
    if (cleaned.startsWith("Online-Exclusive") || cleaned.startsWith("Online Exclusive")) return "Online-Exclusive";
    if (cleaned.startsWith("Gardening Level")) return "Gardening Level";
    
    return cleaned;
  }

  getValue(row, startStr) {
    if (!row) return "-";
    const key = Object.keys(row).find(k => k.toLowerCase().startsWith(startStr.toLowerCase()));
    return key ? (row[key] || "-") : "-";
  }

  getUniqueValues(key, splitByComma = false) {
    const values = new Set();
    this.allRows.forEach(row => {
      let val = row[key];
      if (!val) return;
      if (splitByComma) {
        val.split(",").forEach(v => values.add(v.trim()));
      } else {
        values.add(val.trim());
      }
    });
    return Array.from(values).sort();
  }

  parseHarvestDays(str) {
    const s = String(str || "").toLowerCase();
    if (s.includes("immediate")) return 0;
    
    const match = s.match(/(\d+)/);
    if (!match) return 999;

    let val = parseInt(match[1], 10);
    if (s.includes("year")) val *= 365;
    else if (s.includes("month")) val *= 30;
    else if (s.includes("week")) val *= 7;
    return val;
  }

  // --- HELPER: Removes duplicate products ---
  deduplicateRows(rows) {
      const uniqueProducts = new Map();
      rows.forEach(row => {
          const name = String(row["Product Name"] || "").trim();
          if (name && !uniqueProducts.has(name)) {
              uniqueProducts.set(name, row);
          }
      });
      return Array.from(uniqueProducts.values());
  }

  renderSearch() {
    const wrapper = document.createElement("div");
    wrapper.className = "glossary__search";
    wrapper.innerHTML = `
      <span class="glossary__search-icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="6.25" stroke="#164734" stroke-width="1.5"/>
          <path d="M12.75 12.75L16.25 16.25" stroke="#164734" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </span>
      <input type="text" class="glossary__search-input" placeholder="Search" aria-label="Search products">
      <button type="button" class="glossary__search-clear" aria-label="Clear search" hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 18L18 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18 18L6 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    const input = wrapper.querySelector(".glossary__search-input");
    const clearBtn = wrapper.querySelector(".glossary__search-clear");

    // Debounce only the filtering/re-render — the X toggles immediately below.
    const applySearch = debounce(() => {
      const q = input.value.trim();
      this.searchTerm = q.length >= 3 ? q.toLowerCase() : "";
      this.runFilter();
    }, 150);

    input.addEventListener("input", () => {
      clearBtn.hidden = input.value.length === 0;
      applySearch();
    });

    clearBtn.addEventListener("click", () => {
      input.value = "";
      clearBtn.hidden = true;
      this.searchTerm = "";
      this.runFilter();
      input.focus();
    });

    this.elSearch = wrapper;
    return wrapper;
  }

  renderCategoryNav() {
    const categories = this.getUniqueValues("Category", false);
    if (categories.length === 0) return;

    const navContainer = document.createElement("div");
    navContainer.className = "glossary__category-nav";

    categories.forEach(cat => {
        const btn = document.createElement("button");
        btn.className = "glossary__category-card";
        btn.dataset.category = cat; 
        
        const config = this.categoryImages[cat];
        const defaultImg = config?.default || this.defaultPlaceholder;
        
        btn.innerHTML = `
            <div class="glossary__category-card__img">
                <img src="${defaultImg}" alt="${cat}" loading="lazy">
            </div>
            <span class="glossary__category-card__label">${cat}</span>
        `;

        btn.addEventListener("click", () => {
            if (!this.activeFilters["Category"]) {
                this.activeFilters["Category"] = [];
            }
            const index = this.activeFilters["Category"].indexOf(cat);
            if (index > -1) {
                this.activeFilters["Category"].splice(index, 1);
            } else {
                this.activeFilters["Category"].push(cat);
            }
            if (this.activeFilters["Category"].length === 0) {
                delete this.activeFilters["Category"];
            }
            
            this.syncUI();
            this.runFilter();
        });
        navContainer.appendChild(btn);
    });

    const hero = this.container.querySelector(SELECTORS.HERO);
    if (hero) {
        hero.after(navContainer);
    } else {
        this.container.prepend(navContainer);
    }
    
    this.elCategoryNav = navContainer;
  }

  collapseFilterAccordions() {
    if (!this.elFilterMenu) return;
    const openAccordions = this.elFilterMenu.querySelectorAll("details[open]");
    openAccordions.forEach(acc => acc.removeAttribute("open"));
  }

  renderFilters() {
    const filterConfigs = [
      { key: "Growing Zone", label: "Hardiness Zone", isMulti: true, customClass: "filter-hardiness-zone" },
      { key: "Transplant Month", label: "Transplant Month", isMulti: true, customClass: "filter-transplant-month" },
      { key: "Category", label: "Category", isMulti: false },
      { key: "Gardening Level", label: "Gardening Level", isMulti: true }, 
      { key: "Location", label: "Planting Location", isMulti: true },
      { key: "Light", label: "Light", isMulti: true },
      { key: "Harvest", label: "Days to Harvest", isSlider: true },
      { key: "Season", label: "Best Season to Plant", isMulti: true },
      { key: "Organic", label: "Organic", isMulti: false },
      { key: "Online-Exclusive", label: "Online Exclusive", isMulti: false }
    ];

    let minH = 9999;
    let maxH = 0;
    this.allRows.forEach(r => {
        const days = this.parseHarvestDays(r["Harvest"]);
        if (days < 999) { 
            if (days < minH) minH = days;
            if (days > maxH) maxH = days;
        }
    });
    this.harvestMin = minH === 9999 ? 0 : minH;
    this.harvestMax = maxH === 0 ? 120 : maxH;

    const filterBody = document.createElement("div");
    filterBody.className = "glossary__filter-body";
    filterBody.hidden = true; 
    this.elFilterBody = filterBody;

    const header = document.createElement("div");
    header.className = "glossary__controls-header";

    const toggleBtn = document.createElement("button");
    toggleBtn.className = "glossary__filter-btn";
    toggleBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.75 1L14.8676 12.2245V23L10.1618 18.5102V12.2245L0.75 1H24.75Z" stroke="#f8f1e4" stroke-width="1.5" stroke-linejoin="round"/></svg> Filter';
    const toggleCountSpan = document.createElement("span");
    this.elToggleCount = toggleCountSpan; 
    toggleBtn.appendChild(toggleCountSpan);

    toggleBtn.addEventListener("click", () => {
      filterBody.hidden = !filterBody.hidden;
      toggleBtn.classList.toggle("active", !filterBody.hidden);
      if (filterBody.hidden) {
        this.collapseFilterAccordions();
      }
    });
    this.elToggleBtn = toggleBtn; 

    const clearBtn = document.createElement("button");
    clearBtn.className = "glossary__clear-btn";
    clearBtn.textContent = "Clear All";
    clearBtn.hidden = true; 
    clearBtn.addEventListener("click", () => {
        this.clearAllFilters();
    });
    this.clearBtn = clearBtn;

    header.appendChild(toggleBtn);
    header.appendChild(clearBtn);

    const controlsTop = document.createElement("div");
    controlsTop.className = "glossary__controls-top";
    controlsTop.appendChild(header);
    if (this.elStatus) controlsTop.appendChild(this.elStatus);

    const drawerHeader = document.createElement("div");
    drawerHeader.className = "glossary__drawer-header";
    
    const headerLeft = document.createElement("div");
    headerLeft.style.display = "flex";
    headerLeft.style.flexDirection = "column"; 
    
    const drawerTitle = document.createElement("span");
    drawerTitle.className = "glossary__drawer-title";
    drawerTitle.textContent = "Filters ";
    
    const filterCountSpan = document.createElement("span");
    filterCountSpan.className = "glossary__filter-count";
    this.elFilterCount = filterCountSpan; 
    drawerTitle.appendChild(filterCountSpan);

    const resultCount = document.createElement("span");
    resultCount.className = "glossary__result-count";
    const initialCount = new Set(this.allRows.map(r => r["Product Name"])).size;
    resultCount.textContent = `${initialCount} Products`;
    this.elResultCount = resultCount;

    headerLeft.appendChild(drawerTitle);
    headerLeft.appendChild(resultCount);

    const closeBtn = document.createElement("button");
    closeBtn.className = "glossary__drawer-close";
    closeBtn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 18L18 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M18 18L6 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    closeBtn.ariaLabel = "Close filters";
    closeBtn.addEventListener("click", () => {
       filterBody.hidden = true;
       toggleBtn.classList.remove("active");
       this.collapseFilterAccordions(); 
    });

    drawerHeader.appendChild(headerLeft);
    drawerHeader.appendChild(closeBtn);

    const activeTagsContainer = document.createElement("div");
    activeTagsContainer.className = "glossary__active-tags";
    this.elActiveTags = activeTagsContainer;

    const filterMenu = document.createElement("div");
    filterMenu.className = "glossary__filter-menu";
    this.elFilterMenu = filterMenu;

    filterConfigs.forEach(config => {
      const group = document.createElement("details");
      group.className = "glossary__filter-accordion";
      
      if (config.customClass) {
          group.classList.add(config.customClass);
      }
      
      const summary = document.createElement("summary");
      summary.className = "glossary__filter-accordion__summary";
      
      summary.innerHTML = `<span class="glossary__filter-title" data-label="${config.label}">${config.label}</span><span class="icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 12H18" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path class="plus-vertical" d="M12 18V6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      </span>`;
      
      group.appendChild(summary);

      const optionsContainer = document.createElement("div");
      optionsContainer.className = "glossary__filter-options";

      if (config.isSlider) {
          optionsContainer.classList.add("is-slider-container");
          
          const sliderWrapper = document.createElement("div");
          
          const labelVal = document.createElement("div");
          labelVal.className = "glossary__slider-value";
          labelVal.style.marginBottom = "8px";
          labelVal.style.fontWeight = "bold";
          labelVal.textContent = `Under ${this.harvestMax} days`; 
          
          const range = document.createElement("input");
          range.type = "range";
          range.min = this.harvestMin;
          range.max = this.harvestMax;
          range.value = this.harvestMax;
          range.step = 5;
          range.style.width = "100%";
          range.dataset.key = config.key;

          this.updateSliderFill(range);

          range.addEventListener("input", (e) => {
              const val = parseInt(e.target.value, 10);
              this.updateSliderFill(e.target);

              if (val >= this.harvestMax) {
                  labelVal.textContent = `All days`;
              } else {
                  labelVal.textContent = `Under ${val} days`;
              }
          });

          range.addEventListener("change", (e) => {
              const val = parseInt(e.target.value, 10);
              if (val >= this.harvestMax) {
                  delete this.activeFilters[config.key];
              } else {
                  this.activeFilters[config.key] = val; 
              }
              this.syncUI();
              this.runFilter();
          });

          sliderWrapper.appendChild(labelVal);
          sliderWrapper.appendChild(range);
          
          const rangeLabels = document.createElement("div");
          rangeLabels.style.display = "flex";
          rangeLabels.style.justifyContent = "space-between";
          rangeLabels.style.fontSize = "12px";
          rangeLabels.style.opacity = "0.7";
          rangeLabels.style.marginTop = "4px";
          rangeLabels.innerHTML = `<span>${this.harvestMin}d</span><span>${this.harvestMax}d+</span>`;
          sliderWrapper.appendChild(rangeLabels);

          optionsContainer.appendChild(sliderWrapper);
      } 
      else {
          let options = [];
          
          if (config.key === "Transplant Month") {
              // Predefined list handles rendering sort order perfectly
              options = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
          } else {
              options = this.getUniqueValues(config.key, config.isMulti);
              options = options.filter(v => {
                  const check = String(v).toLowerCase();
                  return check !== 'n/a' && check !== '-';
              });
          }
          
          if (config.key === "Organic" || config.key === "Online-Exclusive") {
              options.sort((a, b) => {
                  if (String(a).toLowerCase() === "yes") return -1;
                  if (String(b).toLowerCase() === "yes") return 1;
                  return 0;
              });
          }

          if (config.key === "Gardening Level") {
              options.sort((a, b) => {
                  const sA = String(a).toLowerCase();
                  const sB = String(b).toLowerCase();
                  const getRank = (str) => {
                      if (str.includes("easy")) return 1;
                      if (str.includes("intermediate")) return 2;
                      if (str.includes("advanced") || str.includes("hard")) return 3;
                      return 4; 
                  };
                  return getRank(sA) - getRank(sB);
              });
          }

          if (config.key === "Growing Zone") {
              options.sort((a, b) => {
                  const getZoneNum = val => {
                      const match = String(val).match(/(\d+)/);
                      return match ? parseInt(match[1], 10) : 999;
                  };
                  const numA = getZoneNum(a);
                  const numB = getZoneNum(b);
                  if (numA !== numB) return numA - numB;
                  return String(a).localeCompare(String(b)); 
              });
          }

          if (options.length === 0) return; 

          options.forEach(opt => {
            if (!opt || opt === "-") return;
            
            const label = document.createElement("label");
            label.className = "glossary__filter-item";
            
            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.value = opt;
            checkbox.dataset.key = config.key; 
            
            checkbox.addEventListener("change", () => {
              this.handleFilterChange(config.key, opt, checkbox.checked);
            });

            label.appendChild(checkbox);
            label.appendChild(document.createTextNode(opt));
            optionsContainer.appendChild(label);
          });
      }

      group.appendChild(optionsContainer);
      filterMenu.appendChild(group);
    });

    const buttonContainer = document.createElement('div');
    buttonContainer.classList.add('glossary__button-container')

    const showResultsBtn = document.createElement("button");
    showResultsBtn.className = "glossary__show-results-btn";
    showResultsBtn.textContent = "Show Results";
    showResultsBtn.addEventListener("click", () => {
       filterBody.hidden = true;
       toggleBtn.classList.remove("active");
       this.collapseFilterAccordions(); 
    });
    this.elShowResultsBtn = showResultsBtn;
    buttonContainer.appendChild(showResultsBtn);
    filterBody.appendChild(drawerHeader);
    filterBody.appendChild(activeTagsContainer);
    filterBody.appendChild(filterMenu);
    filterBody.appendChild(buttonContainer);

    const hardinessBanner = this.elControls.querySelector('.hardiness-zone-banner');

    this.elControls.innerHTML = "";

    // Search sits at the very top of the controls column, above the hardiness banner.
    this.elControls.appendChild(this.renderSearch());

    if (hardinessBanner) {
        this.elControls.appendChild(hardinessBanner);
    }

    this.elControls.appendChild(controlsTop);
    this.elControls.appendChild(filterBody);
  }

  handleFilterChange(key, value, isChecked) {
    if (!this.activeFilters[key]) this.activeFilters[key] = [];
    
    if (isChecked) {
      if (!this.activeFilters[key].includes(value)) {
        this.activeFilters[key].push(value);
      }
    } else {
      this.activeFilters[key] = this.activeFilters[key].filter(v => v !== value);
    }

    if (this.activeFilters[key].length === 0) delete this.activeFilters[key];

    this.syncUI();
    this.runFilter();
  }

  clearAllFilters() {
    this.activeFilters = {};
    this.elFilterMenu.querySelectorAll("input[type='checkbox']").forEach(i => i.checked = false);
    
    const range = this.elFilterMenu.querySelector("input[type='range']");
    if (range) {
        range.value = this.harvestMax;
        this.updateSliderFill(range);
        range.dispatchEvent(new Event('input'));
    }

    this.syncUI();
    this.runFilter();
  }

  syncUI() {
    const hasFilters = Object.keys(this.activeFilters).length > 0;
    if (this.clearBtn) this.clearBtn.hidden = !hasFilters;
    
    let totalCount = 0;
    Object.keys(this.activeFilters).forEach(key => {
        const val = this.activeFilters[key];
        if (Array.isArray(val)) totalCount += val.length;
        else totalCount += 1; 
    });

    if (this.elFilterCount) {
      this.elFilterCount.textContent = totalCount > 0 ? `(${totalCount})` : "";
    }

    if (this.elToggleCount) {
        this.elToggleCount.textContent = totalCount > 0 ? ` (${totalCount})` : "";
    }

    const accordions = this.elFilterMenu.querySelectorAll('.glossary__filter-accordion');
    accordions.forEach(acc => {
        const titleSpan = acc.querySelector('.glossary__filter-title');
        const baseLabel = titleSpan.dataset.label; 
        
        let input = acc.querySelector('input');
        if (!input) return;
        const key = input.dataset.key;
        
        let isActive = false;
        let activeCount = 0;

        if (this.activeFilters[key]) {
            if (Array.isArray(this.activeFilters[key])) {
                activeCount = this.activeFilters[key].length;
                isActive = activeCount > 0;
            } else {
                isActive = true;
                activeCount = 1;
            }
        }
        
        if (isActive) {
            titleSpan.textContent = `${baseLabel} (${activeCount})`;
            titleSpan.style.fontWeight = "700"; 
        } else {
            titleSpan.textContent = baseLabel;
            titleSpan.style.fontWeight = "normal";
        }
    });

    this.elActiveTags.innerHTML = "";

    const allCheckboxes = this.elFilterMenu.querySelectorAll("input[type='checkbox']");
    allCheckboxes.forEach(cb => {
        const key = cb.dataset.key;
        const val = cb.value;
        const isActive = this.activeFilters[key]?.includes(val);
        cb.checked = isActive || false;
    });

    if (this.elCategoryNav) {
        const categoryCards = this.elCategoryNav.querySelectorAll(".glossary__category-card");
        categoryCards.forEach(card => {
            const label = card.dataset.category; 
            const isActive = this.activeFilters["Category"]?.includes(label);
            const imgEl = card.querySelector("img");
            
            const config = this.categoryImages[label];
            const defaultImg = config?.default || this.defaultPlaceholder;
            const activeImg = config?.active || defaultImg;

            if (isActive) {
                card.classList.add("active");
                if (imgEl) imgEl.src = activeImg;
            } else {
                card.classList.remove("active");
                if (imgEl) imgEl.src = defaultImg;
            }
        });
    }

    if (!hasFilters) return;

    Object.keys(this.activeFilters).forEach(key => {
      if (Array.isArray(this.activeFilters[key])) {
          this.activeFilters[key].forEach(val => {
            const tag = document.createElement("button");
            tag.className = "glossary__filter-tag";
            
            let displayVal = val;
            if (key === "Growing Zone") {
                displayVal = "Zone " + val;
            }

            tag.innerHTML = `${displayVal} <span><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 18L18 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M18 18L6 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`;
            tag.title = "Remove filter";
            tag.addEventListener("click", () => {
              this.handleFilterChange(key, val, false);
            });
            this.elActiveTags.appendChild(tag);
          });
      }
      else {
          const val = this.activeFilters[key];
          const tag = document.createElement("button");
          tag.className = "glossary__filter-tag";
          tag.innerHTML = `Under ${val} days <span><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 18L18 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M18 18L6 6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`;
          tag.title = "Remove filter";
          tag.addEventListener("click", () => {
              delete this.activeFilters[key];
              const range = this.elFilterMenu.querySelector(`input[data-key="${key}"]`);
              if (range) {
                  range.value = this.harvestMax;
                  this.updateSliderFill(range); 
                  range.dispatchEvent(new Event('input'));
              }
              this.syncUI();
              this.runFilter();
          });
          this.elActiveTags.appendChild(tag);
      }
    });

    const clearAllTag = document.createElement("button");
    clearAllTag.className = "glossary__filter-tag glossary__filter-tag--clear"; 
    clearAllTag.textContent = "Clear All";
    clearAllTag.addEventListener("click", () => {
      this.clearAllFilters();
    });
    this.elActiveTags.appendChild(clearAllTag);
  }

  runFilter() {
    let filtered = this.allRows;

    Object.keys(this.activeFilters).forEach(key => {
      if (key === "Harvest") {
          const maxDays = this.activeFilters[key];
          filtered = filtered.filter(row => {
              const days = this.parseHarvestDays(row[key]);
              return days <= maxDays;
          });
      } 
      else {
          const selectedOptions = this.activeFilters[key];
          if (!selectedOptions.length) return;

          filtered = filtered.filter(row => {
            const actualValue = String(row[key] || "");

            if (key === "Growing Zone") {
                const rowZone = parseInt(actualValue, 10);
                return selectedOptions.some(opt => parseInt(opt, 10) === rowZone);
            }

            return selectedOptions.some(opt => actualValue.includes(opt));
          });
      }
    });

    // Search evaluates the Product Name field ONLY (case-insensitive, partial match).
    if (this.searchTerm) {
      filtered = filtered.filter(row =>
        String(row["Product Name"] || "").toLowerCase().includes(this.searchTerm)
      );
    }

    filtered = this.deduplicateRows(filtered);

    const groups = this.groupByLetter(filtered);
    this.renderGroups(groups);
    this.setStatus(`${filtered.length} Products`);
    
    if (this.elShowResultsBtn) {
       this.elShowResultsBtn.textContent = `Show ${filtered.length} Results`;
    }
    if (this.elResultCount) {
       this.elResultCount.textContent = `${filtered.length} Products`;
    }

    if (this.elControls) {
        const controlsTop = this.elControls.getBoundingClientRect().top + window.scrollY;
        
        if (window.scrollY > controlsTop) {
            window.scrollTo({
                top: Math.max(0, controlsTop - 30), 
                behavior: 'instant' 
            });
        }
    }
  }

  groupByLetter(rows) {
    const groups = {};
    for (const row of rows) {
      const term = String(row["Product Name"] || "").trim();
      if (!term) continue;
      const first = (term[0] || "").toUpperCase();
      const letter = /^[A-Z]$/.test(first) ? first : "#";
      (groups[letter] ||= []).push(row);
    }
    Object.keys(groups).forEach(k => groups[k].sort((a,b) => String(a["Product Name"]).localeCompare(String(b["Product Name"]))));
    return groups;
  }

  renderGroups(groups) {
    this.elContent.innerHTML = "";
    const frag = document.createDocumentFragment();

    const letters = Object.keys(groups).sort();
    if (letters.length === 0) {
      this.elContent.innerHTML = `<div class="glossary__empty"><svg width="90" height="90" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect width="90" height="90" rx="10" fill="#DAE2CB"/>
<path d="M41.0227 47.0742C33.9977 47.1328 33.4039 48.3621 33.3788 48.429L35.8543 65.6737C38.589 66.6522 41.5244 67.1875 44.5853 67.1875C45.9652 67.1875 47.3201 67.0787 48.6498 66.8697L50.4562 48.6214C50.4562 48.6214 48.24 47.0157 41.031 47.0826L41.0227 47.0742Z" fill="#ABB993"/>
<path d="M55.6583 45.2238L52.748 65.8639C53.141 65.7385 53.5257 65.5963 53.9104 65.4458L56.8124 45.2155H58V44.1868C58 42.5811 56.1517 42.3135 56.1517 42.3135H33.5881C33.5881 42.3135 31.7399 42.5811 31.7399 44.1868V45.2155H55.6583V45.2238Z" fill="#ABB993"/>
<path d="M41.0231 47.0729C48.2321 47.0143 50.4483 48.6117 50.4483 48.6117L48.6419 66.8599C50.0469 66.6425 51.4184 66.2996 52.7398 65.8647L55.6501 45.2246H32.9276L33.3876 48.4277C33.4127 48.3691 33.9981 47.1314 41.0315 47.0729H41.0231Z" fill="#ABB993"/>
<g clip-path="url(#clip0_1042_1969)">
<path d="M44.7778 39.5117V41.508C44.7778 41.9191 45.1089 42.251 45.519 42.251C45.9292 42.251 46.2603 41.9191 46.2603 41.508V38.5655C46.3245 37.2776 46.3097 33.2751 43.918 30.4862C40.9036 26.9741 35.5815 25.9388 35.3542 25.8992C35.1565 25.8645 34.9539 25.9091 34.7909 26.023C34.6278 26.137 34.5191 26.3153 34.4845 26.5184C34.4548 26.7165 33.768 31.4373 36.7873 35.0435C39.2235 37.9563 43.2657 39.1501 44.7728 39.5117H44.7778Z" fill="#ABB993"/>
<path d="M46.9767 34.8008C47.0755 35.306 47.1151 35.7568 47.12 36.1481L50.5297 28.8713C50.6483 28.6236 50.9398 28.5196 51.1869 28.6335C51.434 28.7524 51.5378 29.0447 51.4241 29.2924L47.2683 38.1544V39.1797C47.3078 39.1847 47.3473 39.1897 47.3918 39.1897C47.5104 39.1897 47.6339 39.1599 47.7426 39.1005C48.5778 38.6497 52.8275 36.2274 54.562 32.7351C56.845 28.1382 54.735 23.1004 54.6411 22.8873C54.483 22.5208 54.0679 22.3474 53.6923 22.4861C53.4551 22.5802 47.8118 24.7945 45.5535 29.3468L45.341 29.7828C45.2373 29.9958 45.2373 30.2484 45.3509 30.4565C45.3608 30.4812 46.5863 32.8144 46.9767 34.7958V34.8008Z" fill="#ABB993"/>
</g>
<defs>
<clipPath id="clip0_1042_1969">
<rect width="21.2191" height="19.8045" fill="white" transform="translate(34.4004 22.4453)"/>
</clipPath>
</defs>
</svg>No results found</div>`;
      return;
    }

    for (const l of letters) {
      const list = groups[l];
      const section = document.createElement("div");
      section.className = "glossary__section";
      
      const h = document.createElement("h3");
      h.className = "glossary__letter";
      h.textContent = l;
      section.appendChild(h);

      for (const row of list) {
        const title = row["Product Name"] || "Untitled";
        const desc = row["Description"] || "";
        const level = row["Gardening Level"] || "";
        const imgUrl = row["Image Link"];
        const buyUrl = row["Buy Now Link"];

        const cat = row["Category"] || "-";
        const cycle = this.getValue(row, "Annual");
        const use = this.getValue(row, "Recommended Use");
        const heatCat = this.getValue(row, "Pepper Heat");
        const shu = this.getValue(row, "SHU");
        const heirloom = row["Heirloom"] || "-";
        const aka = row["AKA"] || "-";
        const height = this.getValue(row, "Mature Height");
        const width = this.getValue(row, "Mature Width");
        const compact = this.getValue(row, "Compact");
        const heatTol = row["Heat Tolerant"] || "-";
        const frostTol = row["Frost Tolerant"] || "-";
        const droughtTol = row["Drought"] || "-";
        const disease = this.getValue(row, "Disease Resistant");
        const light = row["Light"] || "-"; 
        const season = row["Season"] || "-"; 
        const location = row["Location"] || "-"; 
        const depth = this.getValue(row, "Planting Depth");
        const spacing = this.getValue(row, "Space between");
        const harvest = row["Harvest"] || "-"; 
        const water = row["Water Requirements"] || "-";
        const fert = row["Fertilizer"] || "-";
        const sold = row["Where It's Sold"] || "-";
        const pets = row["Safe for Pets"] || "-";
        const wildlife = this.getValue(row, "Wildlife");
        
        // Formatted Zone
        const zoneRaw = row["Growing Zone"] || "-"; 
        const zoneMatch = String(zoneRaw).match(/(\d+)/);
        const zone = zoneMatch ? zoneMatch[1] : zoneRaw;
        
        const transplantMonth = row["Transplant Month"] || "-";
        
        const organic = row["Organic"] || "-";
        const onlineExclusive = row["Online-Exclusive"] || "-";

        const renderSpec = (label, val) => {
            const v = String(val || "").trim();
            const check = v.toLowerCase();
            if (!v || check === "-" || check === "n/a" || check === "no") return ""; 
            return `<div class="spec-item"><strong>${label}:</strong> <span>${v}</span></div>`;
        };

        let levelIcons = "";
        const leafSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 12" width="13" height="12" class="level-icon"><path fill="#058a57" d="M6.36,10.34v1.21c0,.25.2.45.45.45s.45-.2.45-.45v-1.78c.04-.78.03-3.21-1.44-4.9C3.98,2.74.72,2.12.58,2.09c-.12-.02-.25,0-.35.08-.1.07-.17.18-.19.3-.02.12-.44,2.98,1.41,5.17,1.49,1.76,3.97,2.49,4.89,2.71h0Z"/><path fill="#058a57" d="M7.7,7.49c.06.31.08.58.09.82l2.09-4.41c.07-.15.25-.21.4-.14.15.07.22.25.15.4l-2.55,5.37v.62s.05,0,.08,0c.07,0,.15-.02.21-.05.51-.27,3.12-1.74,4.18-3.86,1.4-2.79.11-5.84.05-5.97-.1-.22-.35-.33-.58-.24-.15.06-3.6,1.4-4.99,4.16l-.13.26c-.06.13-.06.28,0,.41,0,.02.76,1.43,1,2.63h0Z"/></svg>`;
        
        if (level) {
            const lowerLevel = level.toLowerCase();
            let count = 1; 
            if (lowerLevel.includes("intermediate")) count = 2;
            if (lowerLevel.includes("advanced") || lowerLevel.includes("hard")) count = 3;
            levelIcons = new Array(count).fill(leafSvg).join("");
        }

        const details = document.createElement("details");
        details.className = "glossary__accordion";
        
        const summary = document.createElement("summary");
        summary.className = "glossary__accordion__summary";
        
        summary.innerHTML = `
          <div class="glossary__card-img-col">
             ${imgUrl ? `<img src="${imgUrl}" alt="${title}" loading="lazy">` : '<div class="no-img"></div>'}
          </div>

          <div class="glossary__card-text-col">
            <div class="glossary__card-header">
              <span class="glossary__title">${title}</span>
              <p class="glossary__desc-preview">${desc}</p>
              <div class="glossary__card-meta">
                 ${level ? `
                   <p class="glossary__level">Gardening Level</p>
                   <div class="glossary__level-wrapper">
                     <div class="glossary__level-icons">${levelIcons}</div>
                     <p class="glossary__level-tag" data-level="${level.toLowerCase()}">${level}</p>
                   </div>
                 ` : ''}
              </div>
            </div>
            <button aria-label="open accordion" class="glossary__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 12H18" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path class="plus-vertical" d="M12 18V6" stroke="#164734" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        `;
        
        const content = document.createElement("div");
        content.className = "glossary__accordion__content";
        content.innerHTML = `
          ${buyUrl ? `<a href="${buyUrl}" class="glossary__card__btn" target="_blank"><svg class="" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" role="presentation" xmlns="https://www.w3.org/2000/svg">
      <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6M10 21C10 21.5523 9.55228 22 9 22C8.44772 22 8 21.5523 8 21C8 20.4477 8.44772 20 9 20C9.55228 20 10 20.4477 10 21ZM21 21C21 21.5523 20.5523 22 20 22C19.4477 22 19 21.5523 19 21C19 20.4477 19.4477 20 20 20C20.5523 20 21 20.4477 21 21Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>Buy Now</a>` : ''}

          <div class="glossary__specs-group">
            <h4 class="glossary__specs-header">Plant Info</h4>
            <div class="glossary__card__specs">
              ${renderSpec("Vegetable Family", cat)}
              ${renderSpec("Annual/Perennial", cycle)}
              ${renderSpec("Recommended Use", use)}
              ${renderSpec("Pepper Heat Category", heatCat)}
              ${renderSpec("SHU", shu)}
              ${renderSpec("Heirloom", heirloom)}
              ${renderSpec("AKA", aka)}
            </div>
          </div>
          <div class="glossary__specs-group">
            <h4 class="glossary__specs-header">Growing Info</h4>
            <div class="glossary__card__specs">
              ${renderSpec("Mature Height", height)}
              ${renderSpec("Mature Width", width)}
              ${renderSpec("Compact/Dwarf", compact)}
              ${renderSpec("Growing Zone", zone)} 
              ${renderSpec("Heat Tolerant", heatTol)}
              ${renderSpec("Frost Tolerant", frostTol)}
              ${renderSpec("Drought Tolerant", droughtTol)}
              ${renderSpec("Disease Resistant", disease)}
            </div>
          </div>
          <div class="glossary__specs-group">
            <h4 class="glossary__specs-header">Planting Info</h4>
            <div class="glossary__card__specs">
              ${renderSpec("Light", light)}
              ${renderSpec("Best Season", season)}
              ${renderSpec("Location", location)}
              ${renderSpec("Planting Depth", depth)}
              ${renderSpec("Spacing", spacing)}
              ${renderSpec("Days to Harvest", harvest)}
            </div>
          </div>
          <div class="glossary__specs-group">
            <h4 class="glossary__specs-header">Water & Feeding Needs</h4>
            <div class="glossary__card__specs">
              ${renderSpec("Water Requirements", water)}
              ${renderSpec("Fertilizer", fert)}
            </div>
          </div>
          <div class="glossary__specs-group">
            <h4 class="glossary__specs-header">Retail Info</h4>
            <div class="glossary__card__specs">
              ${renderSpec("Where It's Sold", sold)}
              ${renderSpec("Online Exclusive", onlineExclusive)} </div>
          </div>
          <div class="glossary__specs-group">
            <h4 class="glossary__specs-header">Eco Info</h4>
            <div class="glossary__card__specs">
              ${renderSpec("Safe for Pets", pets)}
              ${renderSpec("Wildlife/Pest Deterred", wildlife)}
              ${renderSpec("Organic", organic)} </div>
          </div>
        `;

        details.appendChild(summary);
        details.appendChild(content);
        section.appendChild(details);
      }
      frag.appendChild(section);
    }
    this.elContent.appendChild(frag);
  }

  loadCsv() {
    this.setStatus("Loading…");
    fetch(this.csvUrl, { credentials: "omit" })
      .then((res) => {
        if (!res.ok) throw new Error(`CSV request failed (${res.status})`);
        return res.text();
      })
      .then((text) => {
        const results = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => this.normalizeHeader(h)
        });
        
        let rawRows = (results.data || []).filter((r) =>
          Object.values(r).some((v) => String(v || "").trim().length)
        );

        // --- MERGE MISSING DATA ACROSS IDENTICAL PRODUCTS ---
        const productMaster = {};
        rawRows.forEach(row => {
            const name = String(row["Product Name"] || "").trim();
            if (!name) return;
            
            if (!productMaster[name]) productMaster[name] = {};
            
            Object.keys(row).forEach(key => {
                const val = String(row[key] || "").trim();
                const check = val.toLowerCase();
                if (val && check !== "-" && check !== "n/a" && check !== "nan") {
                    if (!productMaster[name][key]) {
                        productMaster[name][key] = row[key];
                    }
                }
            });
        });

        this.allRows = rawRows.map(row => {
            const name = String(row["Product Name"] || "").trim();
            if (!name) return row;
            
            const master = productMaster[name];
            const enrichedRow = { ...row };
            
            Object.keys(master).forEach(key => {
                if (key !== "Growing Zone" && key !== "Transplant Month") {
                    const val = String(enrichedRow[key] || "").trim();
                    const check = val.toLowerCase();
                    if (!val || check === "-" || check === "n/a" || check === "nan") {
                        enrichedRow[key] = master[key];
                    }
                }
            });
            return enrichedRow;
        });
        // ----------------------------------------------------

        this.renderCategoryNav();
        this.renderFilters();
        
        const dedupedRows = this.deduplicateRows(this.allRows);
        const groups = this.groupByLetter(dedupedRows);
        this.renderGroups(groups);
        this.setStatus(`${dedupedRows.length} Products`);

        setTimeout(() => {
            this.container.classList.add('is-loaded');
        }, 50); 
      })
      .catch((err) => {
        console.error(err);
        this.setStatus("Failed to load CSV.");
      });
  }
}

class HardinessZoneManager {
    constructor(csvUrl, parentContainer) {
        this.csvUrl = csvUrl;
        this.parentContainer = parentContainer;
        this.zone = localStorage.getItem('userHardinessZone');
        this.zip = localStorage.getItem('userZipCode');
        this.csvData = null; 
        this.container = null;
        
        this.init();
    }

    init() {
        this.injectContainer();
        this.renderBanner();
        this.buildModal();

        if (!this.zone) {
            this.detectZoneFromIP();
        }
    }

    injectContainer() {
        const controls = this.parentContainer.querySelector('.glossary__controls');
        
        if (controls) {
            this.container = document.createElement('div');
            this.container.classList.add('hardiness-zone-banner');
            controls.prepend(this.container); 
        }
    }

    renderBanner() {
        if (!this.container) return;

        if (this.zone) {
            this.container.innerHTML = `
                <div class="zone-info">
                    <span>Your zone: <strong>Zone ${this.zone}</strong></span>
                    <button type="button" class="btn-edit-zone" data-trigger-zone-modal>Edit</button>
                </div>
            `;
        } else {
            this.container.innerHTML = `
                <div class="zone-info">
                    <span>What's your hardiness zone?</span>
                    <button type="button" class="btn-edit-zone" data-trigger-zone-modal>Enter your zip code</button>
                </div>
            `;
        }

        this.container.querySelector('[data-trigger-zone-modal]').addEventListener('click', () => {
            this.openModal();
        });
    }

    buildModal() {
        const modalHtml = `
            <div class="zone-modal-overlay d-none" id="zoneModal">
                <div class="zone-modal-content">
                    <button class="zone-modal-close" id="closeZoneModal">    
                      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true" focusable="false" role="presentation" xmlns="https://www.w3.org/2000/svg">
                        <path d="M9.75 1.25L1.25 9.75M1.25 1.25L9.75 9.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <h3>Find your hardiness zone</h3>
                    <form id="zoneForm">
                        <div class="zone-input-group">
                            <label for="zipInput">Zip code</label>
                            <input type="text" id="zipInput" placeholder="Enter 5-digit zip" maxlength="5" value="${this.zip || ''}" required>
                            <span class="zone-error d-none" id="zoneError">Invalid zip code or zone not found.</span>
                        </div>
                        <button type="submit" class="btn btn-primary w-100" id="zoneSubmitBtn">Submit</button>
                    </form>
                </div>
            </div>
        `;
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        this.modal = document.getElementById('zoneModal');
        this.form = document.getElementById('zoneForm');
        this.errorSpan = document.getElementById('zoneError');
        this.zipInput = document.getElementById('zipInput');
        this.submitBtn = document.getElementById('zoneSubmitBtn');

        document.getElementById('closeZoneModal').addEventListener('click', () => this.closeModal());
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) this.closeModal();
        });

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleFormSubmit();
        });
    }

    openModal() {
        this.modal.classList.remove('d-none');
        this.errorSpan.classList.add('d-none');
    }

    closeModal() {
        this.modal.classList.add('d-none');
    }

    fetchCSV() {
        if (this.csvData) return Promise.resolve(this.csvData); 
        
        return fetch(this.csvUrl)
            .then(response => response.text())
            .then(text => {
                this.csvData = {};
                const lines = text.split('\n');
                for (let i = 1; i < lines.length; i++) { 
                    const [zip, zone] = lines[i].split(',');
                    if (zip && zone) {
                        this.csvData[zip.trim()] = zone.trim();
                    }
                }
                return this.csvData;
            })
            .catch(e => {
                console.error("Error fetching Hardiness CSV:", e);
                return null;
            });
    }

    lookupZone(zipcode) {
        return this.fetchCSV().then(data => {
            if (data && data[zipcode]) {
                return data[zipcode];
            }
            return null;
        });
    }

    detectZoneFromIP() {
        fetch('https://ipapi.co/json/')
            .then(response => response.json())
            .then(data => {
                if (data && data.postal) {
                    const detectedZip = data.postal;
                    this.lookupZone(detectedZip).then(foundZone => {
                        if (foundZone) {
                            this.saveZoneData(detectedZip, foundZone);
                        }
                    });
                }
            })
            .catch(e => {
                console.log("IP detection failed or blocked by adblocker.", e);
            });
    }

    handleFormSubmit() {
        const zipValue = this.zipInput.value.trim();
        const zipRegex = /^\d{5}$/;
        
        if (!zipRegex.test(zipValue)) {
            this.showError("Please enter a valid 5-digit zip code.");
            return;
        }

        this.submitBtn.textContent = 'Looking up...';
        this.submitBtn.disabled = true;

        this.lookupZone(zipValue).then(foundZone => {
            this.submitBtn.textContent = 'Submit';
            this.submitBtn.disabled = false;

            if (foundZone) {
                this.saveZoneData(zipValue, foundZone);
                this.closeModal();
            } else {
                this.showError("Zone not found for this zip code.");
            }
        });
    }

    saveZoneData(zip, zone) {
        this.zip = zip;
        this.zone = zone;
        localStorage.setItem('userZipCode', zip);
        localStorage.setItem('userHardinessZone', zone);
        this.renderBanner(); 
    }

    showError(msg) {
        this.errorSpan.textContent = msg;
        this.errorSpan.classList.remove('d-none');
    }
}

document.addEventListener("DOMContentLoaded", () => {
  const containers = document.querySelectorAll(SELECTORS.CONTAINER);
  
  containers.forEach((container) => {
    new WsGlossary(container).init();

    const hardinessCsvUrl = container.getAttribute(DATA_ATTRS.ZONE_CSV_URL);
    
    if (hardinessCsvUrl) {
      new HardinessZoneManager(hardinessCsvUrl, container); 
    }
  });
});
